import api from '@api';
import PromptInput from '@components/layout/main/PromptInput';
import { Log } from '@enums';
import { useWindowSize } from '@hooks';
import CheckIcon from '@icons/check.svg?react';
import ErrorIcon from '@icons/error.svg?react';
import InfoIcon from '@icons/info-message.svg?react';
import { useChatStore } from '@store/chat';
import { useShellStore } from '@store/shell';
import { Notification as Toast } from '@ui/notification';
import { getQuotaStatusText, getStorageItem, log } from '@utils';
import type { ForwardedRef } from 'react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import type { Dataset, PromptUsage, RelevantAudience } from 'types/schema';
import Styled from './Prompt.styled';

interface Props {
  autoFocus?: boolean;
  className?: string;
  quota: PromptUsage;
  shouldAnimateQuotaStatus?: boolean;
  isLoading?: boolean;
  onSubmit: (
    message: string,
    origin: 'click' | 'enter',
    audiences: RelevantAudience[]
  ) => void;
  onCancel: () => void;
  onChangeDataset?: (dataset: Dataset) => void;
}

function Prompt(props: Props, ref: ForwardedRef<HTMLTextAreaElement>) {
  const shellStore = useShellStore();
  const isLoading = useChatStore((state) => state.isLoading);
  const sessionIsLoading = useChatStore((state) => state.sessionIsLoading);
  const getMessageCount = useChatStore((state) => state.getMessageCount);
  const chat = useChatStore((state) => state.chat);
  const setSelectedDataset = useChatStore((state) => state.setSelectedDataset);
  const selectedDataset = useChatStore((state) => state.selectedDataset);
  const setSelectedAudiences = useChatStore(
    (state) => state.setSelectedAudiences
  );
  const windowSize = useWindowSize();
  const [datasets, setDatasets] = useState<Dataset[]>(
    getStorageItem('octopus.available_datasets', sessionStorage) || []
  );
  const datasetsFetchedRef = useRef(false);
  const gradientRef = useRef<HTMLDivElement>(null);
  const [quotaStatusText, setQuotaStatus] = useState(
    getQuotaStatusText(props.quota)
  );
  const hasQuota = props.quota['usage_remaining'] !== -1;
  const hasZeroPrompts = props.quota['usage_remaining'] === 0;
  const isDisabled = isLoading || sessionIsLoading || hasZeroPrompts;

  const placeholderText = useMemo(() => {
    if (hasZeroPrompts) {
      return 'Upgrade to chat with Agent Spark';
    }

    if (isLoading || sessionIsLoading) {
      return '';
    }

    return 'Chat with Agent Spark';
  }, [hasZeroPrompts, isLoading, sessionIsLoading]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: OK
  useEffect(() => {
    setQuotaStatus(getQuotaStatusText(props.quota));
  }, [props.quota, windowSize.value]);

  useEffect(() => {
    // Avoid loop when datasets are already set
    if (datasets.length > 0) {
      if (selectedDataset) {
        return;
      }

      const savedDataset = getStorageItem<Dataset>(
        'octopus.dataset',
        sessionStorage
      );
      const currentDataset = savedDataset || datasets[0];

      if (!currentDataset) {
        return;
      }

      setSelectedDataset(currentDataset);

      window.sessionStorage.setItem(
        'octopus.dataset',
        JSON.stringify(currentDataset)
      );

      log('Selected dataset:', { data: currentDataset, type: Log.Debug });
      return;
    }

    // Fetch the available datasets at most once per mount
    if (datasetsFetchedRef.current) {
      return;
    }
    datasetsFetchedRef.current = true;

    api.user.getAccessibleDatasets().then((availableDatasets) => {
      if (!availableDatasets.length) {
        // Allow a retry on the next render
        datasetsFetchedRef.current = false;
        return;
      }

      setDatasets(availableDatasets);

      window.sessionStorage.setItem(
        'octopus.available_datasets',
        JSON.stringify(availableDatasets)
      );

      const savedDataset = getStorageItem<Dataset>(
        'octopus.dataset',
        sessionStorage
      );
      const activeDataset = savedDataset || availableDatasets[0];

      if (activeDataset) {
        setSelectedDataset(activeDataset);

        window.sessionStorage.setItem(
          'octopus.dataset',
          JSON.stringify(activeDataset)
        );
      }
    });
  }, [datasets, selectedDataset, setSelectedDataset]);

  const handleConfirmDatasetChange = useCallback(
    (dataset: Dataset) => {
      // Update selected dataset in store
      setSelectedDataset(dataset);

      // Reset selected audiences
      setSelectedAudiences([]);

      log('Reset selected audiences.');

      // Notify parent about dataset change
      props.onChangeDataset?.(dataset);

      // Persist dataset in sessionStorage
      window.sessionStorage.setItem(
        'octopus.dataset',
        JSON.stringify(dataset)
      );

      // Reset cached suggested audiences
      window.sessionStorage.removeItem('octopus.suggested_audiences');
    },
    [setSelectedAudiences, setSelectedDataset, props.onChangeDataset]
  );

  const handleAudienceChange = useCallback(
    (
      _action: 'add' | 'remove',
      _audience: RelevantAudience,
      selectedAudiences?: RelevantAudience[]
    ) => {
      if (selectedAudiences.length > 0) {
        gradientRef.current?.classList.add('extended');
      } else {
        gradientRef.current?.classList.remove('extended');
      }
    },
    []
  );

  const handleNotificationClose = useCallback(() => {
    shellStore.setToast({ isOpen: false, message: '' });
  }, [shellStore.setToast]);

  return (
    <Styled.Wrapper>
      {getMessageCount() > 0 && <Styled.Gradient ref={gradientRef} />}
      <Styled.StatusWrapper
        data-testid="status-wrapper"
        hasZeroPrompts={hasZeroPrompts}
        hidden={!hasQuota}
        shouldAnimate={props.shouldAnimateQuotaStatus}>
        <Styled.Status data-testid="status">
          <InfoIcon />
          <span>{quotaStatusText}</span>
        </Styled.Status>
      </Styled.StatusWrapper>
      <PromptInput
        autoFocus={props.autoFocus}
        className={props.className}
        datasets={datasets}
        disabled={isDisabled}
        chat={chat}
        isLoading={props.isLoading}
        placeholder={placeholderText}
        ref={ref}
        selectedDataset={selectedDataset}
        onAudienceChange={handleAudienceChange}
        onCancel={props.onCancel}
        onConfirmDatasetChange={handleConfirmDatasetChange}
        onSubmit={props.onSubmit}
      />
      <Toast
        className="toast"
        icon={
          shellStore.toast.status === 'error' ? <ErrorIcon /> : <CheckIcon />
        }
        message={shellStore.toast.message}
        open={shellStore.toast.isOpen}
        position={shellStore.toast.position}
        buttonText={shellStore.toast.buttonText}
        onButtonClick={shellStore.toast.onButtonClick}
        timeout={shellStore.toast.timeout}
        onNotificationClose={handleNotificationClose}
        status={shellStore.toast.status}
      />
    </Styled.Wrapper>
  );
}

export default forwardRef(Prompt);
